import { Stack } from "@mantine/core"
import { DatePickerInput } from "@mantine/dates"
import { useTranslation } from "react-i18next"
import type { TEditStepProps } from "@/components/activities/types"
import { RRuleInput, TimeInputs } from "@/components/forms"
import { icons } from "@/components/utils"
import type { TSession } from "@/types"
import { addWorkdays, dayjs, nextHour, rrules } from "@/utils"
import { clientTimezone } from "@/utils/env"
import type { IStepInitialValues, IStepProps } from "./types"
import { PreviewRow } from "./utils"

export function DateTimeStep({ form }: IStepProps) {
  const { t } = useTranslation()

  return (
    <Stack>
      <DatePickerInput
        w={400}
        required
        type="range"
        numberOfColumns={2}
        minDate={new Date()}
        valueFormat="D MMM YYYY"
        label={t("Run Dates")}
        popoverProps={{ withinPortal: true }}
        {...form.getInputProps("dates")}
      />

      <TimeInputs form={form} w={400} tzField="meta.tz" required />
      <RRuleInput w={400} {...form.getInputProps("rrule")} />
    </Stack>
  )
}

DateTimeStep.initialValues = ({ session }: IStepInitialValues) => {
  const tz = session?.meta.tz ?? clientTimezone
  const dtstart = session?.dtstart ? dayjs(session.dtstart) : nextHour()
  const dtfinish = session?.dtfinish ? dayjs(session.dtfinish) : addWorkdays(dtstart, 5)

  return {
    dtstart: dtstart.valueOf(),
    dtfinish: dtfinish.valueOf(),
    dates: [dtstart.format("YYYY-MM-DD"), dtfinish.format("YYYY-MM-DD")],
    meta: session?.meta || { tz },
    rrule: session?.rrule ?? null,
    time: dayjs(dtstart).format("HH:mm"),
  }
}

DateTimeStep.validate = ({ dates, meta }: any, t) => {
  return {
    dates: dates?.length !== 2 || !dates[0] || !dates[1] ? t("Please select a start and end date") : null,
    "meta.tz": !meta?.tz ? t("Please select a timezone") : null,
  }
}

DateTimeStep.transformValues = ({ dates, time, meta, ...values }: TSession & { dates: string[]; time: string }) => {
  const dtstart = dayjs(`${dates[0]} ${time}`).tz(meta.tz, true).valueOf()
  const dtfinish = dayjs(`${dates[1]} ${time}`).tz(meta.tz, true).valueOf()

  return { ...values, dtstart, dtfinish, meta }
}

DateTimeStep.label = "Date/Time"

DateTimeStep.Preview = ({ form }: TEditStepProps) => {
  const { rrule, ...values } = form.values as any

  const { t } = useTranslation()

  const repeat = rrule ? rrules.find((r) => r.value === rrule)?.label : null
  const dateValues = DateTimeStep.transformValues(values as any)
  return (
    <>
      <PreviewRow value={dayjs(dateValues.dtstart).format("YYYY-MM-DD HH:mm")}>
        <icons.Calendar />
        {t("Start Date")}
      </PreviewRow>
      <PreviewRow value={dayjs(dateValues.dtfinish).format("YYYY-MM-DD HH:mm")}>
        <icons.Calendar />
        {t("End Date")}
      </PreviewRow>
      <PreviewRow value={values.meta.tz}>
        <icons.Timezone />
        {t("Timezone")}
      </PreviewRow>
      <PreviewRow value={repeat ? t(repeat) : t("No repeat")}>
        <icons.Recurrence />
        {t("Recurrence")}
      </PreviewRow>
    </>
  )
}
